import { RichTextAdvancedInput } from '@framework/engine/inputs/rich-text-advanced/RichTextAdvancedInput.tsx';
import { Datatype } from '@framework/engine/schema/expressions/datatype/Datatype.ts';
import { ReferenceType } from '@framework/engine/schema/expressions/references/IReference.ts';
import { ExpressionReferenceInput } from '@framework/engine/schema/expressions/references/input/ExpressionReferenceInput.tsx';
import { type IWidgetComponentProps } from '@framework/engine/schema/widgets/IWidgetType.ts';
import { type IWidgetConfigurationSlot } from '@framework/engine/schema/widgets/repository/common/config-slots/IWidgetConfigurationSlot.ts';
import { WidgetConfigurationSlotRender } from '@framework/engine/schema/widgets/repository/common/config-slots/WidgetConfigurationSlotRender.tsx';
import { WidgetConfigurationSlots } from '@framework/engine/schema/widgets/repository/common/config-slots/WidgetConfigurationSlots.tsx';
import { CommonConfigComponent } from '@framework/engine/schema/widgets/repository/inputs/common/CommonInputConfigComponent.tsx';
import { HideLabelConfigInput } from '@framework/engine/schema/widgets/repository/inputs/common/HideLabelConfigInput.tsx';
import { useTreeReferencesContext } from '@framework/engine/tree/references/TreeReferencesContext.tsx';
import { MenuPresets } from '@framework/form/inputs/rich-text/menu/MenuPresets.tsx';
import { SelectInput } from '@framework/form/inputs/SelectInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { WidgetConfigurationSubCard } from '@framework/ui/components/configuration-card/widget/WidgetConfigurationSubCard.tsx';
import { TbIcon } from '@framework/ui/components/icon/TbIcon.tsx';
import { IconShieldLock, IconUpload } from '@tabler/icons-react';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { UploadWidgetAgidConfigForm } from '#features/packages/xsd-web/src/widgets/agid-upload/UploadWidgetAgidConfigForm.tsx';
import { type IUploadWidget, UploadButtonsLayout } from './IUploadWidget.ts';

/** Card per i testi del caricamento pubblico quando il dato è sensibile (omissis). */
const UploadSensitiveDataSlot: IWidgetConfigurationSlot = {
  key: 'upload-sensitive-data',
  title: 'Impostazioni pulsante upload per Dati Sensibili',
  icon: <TbIcon icon={IconShieldLock} />,
  description:
    'Testi e layout del caricamento del documento pubblico per i dati sensibili.',
  order: 15,
};

const TFormItem = FormItem as typeof FormItem<IUploadWidget>;

export type IUploadWidgetConfigComponentProps =
  IWidgetComponentProps<IUploadWidget>;

export function UploadWidgetConfigComponent(
  props: IUploadWidgetConfigComponentProps,
) {
  const sensitiveDataUpload = useWatch({
    name: 'directives.omissis.enabled',
    defaultValue: false,
  });

  const context = useTreeReferencesContext();

  const references = useMemo(() => {
    return context.references.filter(
      r => r.field.type === Datatype.File && r.type !== ReferenceType.Widget,
    );
  }, [context.references]);

  return (
    <>
      {/* Tipo (kind) non configurabile: unica opzione disponibile (Button),
          default applicato da uploadInputConfigSchema alla creazione. */}

      <WidgetConfigurationSubCard
        icon={<TbIcon icon={IconUpload} />}
        title="Titolo del pulsante"
      >
        <TFormItem
          name="title"
          label="Titolo"
          help={
            <>
              <b>Titolo del pulsante</b> di upload, se non inserito verrà
              mostrata soltanto un&apos;icona di upload
            </>
          }
          layout={{ hideVisualLabel: true, removeMargin: true }}
        >
          <RichTextAdvancedInput
            name="title"
            rows={2}
            menus={MenuPresets.Minimal}
          />
        </TFormItem>
      </WidgetConfigurationSubCard>

      {/* Configurazioni comuni */}
      <CommonConfigComponent
        {...props}
        hideConfigPlaceholder
        hideConfigDefaultValue
      />

      <WidgetConfigurationSlotRender
        slot={WidgetConfigurationSlots.Default}
        order={10}
      >
        <TFormItem
          name="defaultValueReference"
          label="Documento predefinito"
          help="Configurazione opzionale per il caricamento di un documento predefinito in riferimento a un file contestuale."
        >
          <ExpressionReferenceInput
            size="large"
            name="defaultValueReference"
            references={references}
            width="100%"
          />
        </TFormItem>
      </WidgetConfigurationSlotRender>

      <WidgetConfigurationSlotRender
        slot={WidgetConfigurationSlots.Layout}
        order={30}
      >
        <HideLabelConfigInput />
      </WidgetConfigurationSlotRender>

      {sensitiveDataUpload && (
        <WidgetConfigurationSlotRender slot={UploadSensitiveDataSlot}>
          <TFormItem name="publicUploadTitle" label="Titolo documento pubblico">
            <RichTextAdvancedInput
              name="publicUploadTitle"
              rows={2}
              menus={MenuPresets.Minimal}
            />
          </TFormItem>
          <TFormItem
            name="publicUploadLabel"
            label="Etichetta documento pubblico"
          >
            <RichTextAdvancedInput
              name="publicUploadLabel"
              rows={2}
              menus={MenuPresets.Minimal}
            />
          </TFormItem>
          <TFormItem
            name="publicHelpText"
            label="Testo di aiuto documento pubblico"
          >
            <RichTextAdvancedInput
              name="publicHelpText"
              rows={2}
              menus={MenuPresets.Minimal}
            />
          </TFormItem>

          <TFormItem name="layout.buttonLayout" label="Posizionamento pulsante">
            <SelectInput
              name="layout.buttonLayout"
              defaultValue={UploadButtonsLayout.Vertical}
              options={[
                {
                  label: 'In Verticale | Pulsanti sovrapposti',
                  value: UploadButtonsLayout.Vertical,
                },
                {
                  label: 'In Orizzontale | Pulsanti affiancati',
                  value: UploadButtonsLayout.Horizontal,
                },
              ]}
            />
          </TFormItem>
        </WidgetConfigurationSlotRender>
      )}
      {/* Dichiara da sé la propria card: a flag spento è un componente vuoto,
          quindi nessuna card */}
      {BuildFlags.BUILD_FLAGS_AGID_XSD && <UploadWidgetAgidConfigForm />}
    </>
  );
}
